import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, FileCode2, Search, ExternalLink } from "lucide-react";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

const STATUS_STYLE = {
  Permitted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Conditions: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Rejected: "bg-rose-100 text-rose-800 border-rose-200",
  Withdrawn: "bg-rose-100 text-rose-800 border-rose-200",
  Undecided: "bg-amber-100 text-amber-800 border-amber-200",
  Referred: "bg-amber-100 text-amber-800 border-amber-200",
  Unresolved: "bg-amber-100 text-amber-800 border-amber-200",
  Unknown: "bg-muted text-muted-foreground border-border",
};

function formatDMY(isoDate) {
  if (!isoDate) return "—";
  const parts = String(isoDate).slice(0, 10).split("-");
  if (parts.length !== 3) return isoDate;
  const [y, m, d] = parts;
  return `${d}/${m}/${y}`;
}

/**
 * Admin -> Reports -> Planning Applications. Searches PlanIt
 * (planit.org.uk) live, by postcode — same endpoint the agent dashboard's
 * standalone Planning Applications search uses.
 */
export default function PlanningApplicationsAdmin() {
  const [postcode, setPostcode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    const pc = postcode.trim();
    if (!pc) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/property-admin/planning-applications/search`,
        { postcode: pc },
        { withCredentials: true }
      );
      setResult(data);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Failed to search planning applications");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="planning-applications-admin">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <FileCode2 className="h-6 w-6" /> Planning Applications
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Live lookup from PlanIt (planit.org.uk) — free public UK planning applications data.
          </p>
        </div>
        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              placeholder="Search postcode…"
              className="text-sm border border-input rounded px-2 py-1.5 pl-7 bg-background w-44"
              data-testid="planning-applications-postcode-search"
            />
          </div>
          <button type="submit" className="text-sm px-3 py-1.5 border border-input rounded hover:bg-muted/40" data-testid="planning-applications-search-btn">
            Search
          </button>
        </form>
      </div>

      {loading && (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && result && (
        <section className="border border-border rounded bg-background overflow-x-auto" data-testid="planning-applications-results">
          {result.applications.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="planning-applications-empty">
              No planning applications found near {result.postcode}.
            </div>
          ) : (
            <table className="w-full text-sm min-w-[860px]">
              <thead className="text-left text-muted-foreground text-xs bg-muted/40">
                <tr>
                  <th className="p-3">Address</th>
                  <th className="p-3">Description</th>
                  <th className="p-3">Authority</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Date</th>
                  <th className="p-3">Link</th>
                </tr>
              </thead>
              <tbody>
                {result.applications.map((a, i) => (
                  <tr key={i} className="border-t border-border hover:bg-muted/20" data-testid={`planning-application-row-${i}`}>
                    <td className="p-3 max-w-[220px] truncate" title={a.address}>{a.address}</td>
                    <td className="p-3 max-w-[300px] truncate" title={a.description}>{a.description}</td>
                    <td className="p-3 text-xs">{a.authority}</td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-1 rounded border ${STATUS_STYLE[a.status] || STATUS_STYLE.Unknown}`}>
                        {a.status || "Unknown"}
                      </span>
                    </td>
                    <td className="p-3 whitespace-nowrap text-xs">{formatDMY(a.date)}</td>
                    <td className="p-3">
                      {a.link ? (
                        <a href={a.link} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1" data-testid={`planning-application-link-${i}`}>
                          View <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}
